import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getClientProfile,
  updateClientProfile,
  deleteClientProfile,
  ClientProfileUpdate,
} from '../services/trustline';
import { useAuth } from '../contexts/AuthContext';
import { extractClientProfile } from '../utils/clientProfile';
import ConfirmDeleteModal from '../components/ConfirmDeleteModal';
import { countryOptions } from '../utils/countries';
import { LOCAL_ONLY_HINT, readLocalAccount, writeLocalAccount } from '../utils/localAccount';
import '../components/layout/DashboardLayout.css';

const emptyAddress = {
  line1: '',
  line2: '',
  city: '',
  stateOrRegion: '',
  postalCode: '',
  country: '',
};

const ClientProfilePage: React.FC = () => {
  const { email, signOut } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const [vatNumber, setVatNumber] = useState('');
  const [countryOfRegistration, setCountryOfRegistration] = useState('');
  const [registrationNumber, setRegistrationNumber] = useState('');
  const [countries] = useState(countryOptions);
  const [form, setForm] = useState<ClientProfileUpdate>({
    type: 'organization',
    firstName: '',
    lastName: '',
    entityName: '',
    title: '',
    email: '',
    address: { ...emptyAddress },
  });

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      const res = await getClientProfile();
      if (cancelled) return;
      if (res.success) {
        const p = extractClientProfile(res.data);
        if (p) {
          setForm({
            type: p.type === 'individual' ? 'individual' : 'organization',
            firstName: p.firstName || '',
            lastName: p.lastName || '',
            entityName: p.entityName || '',
            title: p.title || '',
            email: p.email || email,
            address: { ...emptyAddress, ...p.address },
          });
        } else {
          setForm((prev) => ({ ...prev, email }));
        }
      } else {
        setError(res.error || 'Failed to load profile');
        setForm((prev) => ({ ...prev, email }));
      }
      const local = readLocalAccount(email);
      setVatNumber(local.vatNumber || '');
      setCountryOfRegistration(local.countryOfRegistration || '');
      setRegistrationNumber(local.registrationNumber || '');
      setLoading(false);
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [email]);

  const setField = (key: keyof ClientProfileUpdate, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const setAddressField = (key: string, value: string) => {
    setForm((prev) => ({
      ...prev,
      address: { ...prev.address, [key]: value },
    }));
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);
    const isBusiness = form.type !== 'individual';
    writeLocalAccount(email, {
      vatNumber: isBusiness ? vatNumber.trim() : '',
      countryOfRegistration: isBusiness ? countryOfRegistration : '',
      registrationNumber: isBusiness ? registrationNumber.trim() : '',
    });
    const res = await updateClientProfile({
      type: form.type || undefined,
      firstName: form.firstName || undefined,
      lastName: form.lastName || undefined,
      entityName: isBusiness ? form.entityName || undefined : undefined,
      title: form.title || undefined,
      email: form.email || undefined,
      address: form.address,
    });
    setSaving(false);
    if (res.success) {
      setSuccess('Profile updated.');
    } else {
      setError(res.error || 'Update failed');
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    setDeleteError(null);
    const res = await deleteClientProfile();
    setDeleting(false);
    if (res.success) {
      setDeleteOpen(false);
      await signOut();
      navigate('/login', { replace: true });
    } else {
      setDeleteError(res.error || 'Delete failed');
    }
  };

  if (loading) {
    return <div className="loading">Loading profile…</div>;
  }

  const isBusiness = form.type !== 'individual';

  return (
    <div>
      <div className="dash-page-header">
        <h2>Account Details</h2>
        <p>View and update your Trustline client information.</p>
      </div>

      {error && <div className="dash-error">{error}</div>}
      {success && <div className="dash-success">{success}</div>}

      <form className="dash-card" onSubmit={handleSave}>
        <div className="dash-form-group">
          <label htmlFor="client-type">Account type</label>
          <select id="client-type" value={form.type || 'organization'} onChange={(e) => setField('type', e.target.value)}>
            <option value="organization">Business</option>
            <option value="individual">Individual</option>
          </select>
        </div>

        {isBusiness && (
          <>
            <div className="dash-form-group">
              <label htmlFor="entityName">Entity name</label>
              <input id="entityName" value={form.entityName || ''} onChange={(e) => setField('entityName', e.target.value)} />
            </div>
            <div className="dash-form-row">
              <div className="dash-form-group">
                <label htmlFor="countryOfRegistration">Country of registration</label>
                <select
                  id="countryOfRegistration"
                  value={countryOfRegistration}
                  onChange={(e) => setCountryOfRegistration(e.target.value)}
                >
                  <option value="">Select a country</option>
                  {countries.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="dash-form-group">
                <label htmlFor="registrationNumber">Registration number</label>
                <input id="registrationNumber" value={registrationNumber} onChange={(e) => setRegistrationNumber(e.target.value)} />
              </div>
            </div>
            <div className="dash-form-group">
              <label htmlFor="vatNumber">VAT number</label>
              <input
                id="vatNumber"
                value={vatNumber}
                onChange={(e) => setVatNumber(e.target.value)}
                aria-describedby="vatNumber-hint"
              />
              <small id="vatNumber-hint" className="dash-field-hint">
                Registration country and number, and VAT number: {LOCAL_ONLY_HINT.charAt(0).toLowerCase() + LOCAL_ONLY_HINT.slice(1)}
              </small>
            </div>
            <h3 style={{ marginTop: '1.5rem' }}>Account manager</h3>
          </>
        )}

        <div className="dash-form-row dash-form-row-person">
          <div className="dash-form-group">
            <label htmlFor="title">Title</label>
            <input id="title" value={form.title || ''} placeholder="Ms, Mr, Dr…" onChange={(e) => setField('title', e.target.value)} />
          </div>
          <div className="dash-form-group">
            <label htmlFor="firstName">First name</label>
            <input id="firstName" value={form.firstName || ''} onChange={(e) => setField('firstName', e.target.value)} />
          </div>
          <div className="dash-form-group">
            <label htmlFor="lastName">Last name</label>
            <input id="lastName" value={form.lastName || ''} onChange={(e) => setField('lastName', e.target.value)} />
          </div>
        </div>

        <div className="dash-form-group">
          <label htmlFor="client-email">Email</label>
          <input
            id="client-email"
            type="email"
            value={form.email || ''}
            onChange={(e) => setField('email', e.target.value)}
          />
        </div>

        <h3 style={{ marginTop: '1.5rem' }}>{isBusiness ? 'Business address' : 'Address'}</h3>
        <div className="dash-form-group">
          <label htmlFor="line1">Line 1</label>
          <input
            id="line1"
            value={form.address?.line1 || ''}
            onChange={(e) => setAddressField('line1', e.target.value)}
          />
        </div>
        <div className="dash-form-group">
          <label htmlFor="line2">Line 2</label>
          <input
            id="line2"
            value={form.address?.line2 || ''}
            onChange={(e) => setAddressField('line2', e.target.value)}
          />
        </div>
        <div className="dash-form-row">
          <div className="dash-form-group">
            <label htmlFor="city">City</label>
            <input
              id="city"
              value={form.address?.city || ''}
              onChange={(e) => setAddressField('city', e.target.value)}
            />
          </div>
          <div className="dash-form-group">
            <label htmlFor="postalCode">Postal code</label>
            <input
              id="postalCode"
              value={form.address?.postalCode || ''}
              onChange={(e) => setAddressField('postalCode', e.target.value)}
            />
          </div>
        </div>
        <div className="dash-form-row">
          <div className="dash-form-group">
            <label htmlFor="stateOrRegion">State / region</label>
            <input
              id="stateOrRegion"
              value={form.address?.stateOrRegion || ''}
              onChange={(e) => setAddressField('stateOrRegion', e.target.value)}
            />
          </div>
          <div className="dash-form-group">
            <label htmlFor="country">Country</label>
            <input
              id="country"
              value={form.address?.country || ''}
              onChange={(e) => setAddressField('country', e.target.value)}
            />
          </div>
        </div>

        <div className="dash-actions">
          <button type="submit" className="dash-btn dash-btn-primary" disabled={saving}>
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </form>

      <div className="dash-danger-zone">
        <h3>Danger zone</h3>
        <p>
          <strong>Deleting your client profile is irreversible.</strong> This permanently
          removes your profile, all dApp configurations, all policy mappings, and all
          policies associated with this account.
        </p>
        <p>There is no recovery. Downstream integrations relying on this client will break.</p>
        <button
          type="button"
          className="dash-btn dash-btn-danger"
          onClick={() => {
            setDeleteError(null);
            setDeleteOpen(true);
          }}
        >
          Delete client profile
        </button>
      </div>

      <ConfirmDeleteModal
        open={deleteOpen}
        title="Delete entire client profile?"
        description={
          <>
            <p>
              This will cascade-delete <strong>all dApps</strong>, <strong>all mappings</strong>,
              and <strong>all policies</strong> for this client.
            </p>
            <p>This action cannot be undone.</p>
          </>
        }
        requiredPhrase="I want to delete my client profile"
        confirmLabel="Delete everything"
        loading={deleting}
        error={deleteError}
        onCancel={() => setDeleteOpen(false)}
        onConfirm={handleDelete}
      />
    </div>
  );
};

export default ClientProfilePage;
